/**
 * Consultation, chat s AI poradcem + eskalace na živého poradce.
 * Klient: píše, AI odpovídá; tlačítkem se spojí s poradcem M&Ai.
 * Tým/admin: vidí vlákna (eskalovaná zvýrazněná) a odpovídá přímo v systému.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  listConsultations, createConsultation, getConsultationThread,
  postConsultationMessage, escalateConsultation,
} from '../lib/apiFetch';

const ACCENT = 'var(--purple-glow)';

function fmtTime(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

const SENDER_LABEL = { client: 'Klient', ai: 'AI poradce', team: 'Poradce M&Ai' };

function Bubble({ m, mine }) {
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: '0.6rem' }}>
      <div style={{ maxWidth: '82%' }}>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', margin: mine ? '0 0.2rem 0.15rem auto' : '0 0 0.15rem 0.2rem', textAlign: mine ? 'right' : 'left' }}>
          {SENDER_LABEL[m.sender] || m.sender} · {fmtTime(m.created_at)}
        </div>
        <div style={{
          padding: '0.6rem 0.8rem', borderRadius: 12, fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap',
          background: mine ? ACCENT : (m.sender === 'ai' ? 'var(--purple-deep)' : 'var(--purple-deep)'),
          color: mine ? 'white' : 'var(--star-white)',
        }}>{m.text}</div>
      </div>
    </div>
  );
}

function Thread({ messages, mineSender, bodyRef }) {
  return (
    <div ref={bodyRef} style={{ background: 'var(--purple-deep)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '1rem', minHeight: 260, maxHeight: 460, overflowY: 'auto', marginBottom: '0.75rem' }}>
      {messages.length === 0 && <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>Zatím žádné zprávy.</div>}
      {messages.map(m => <Bubble key={m.id} m={m} mine={m.sender === mineSender} />)}
    </div>
  );
}

// ── Klient ────────────────────────────────────────────────────────────────────
function ClientConsultation() {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const bodyRef = useRef(null);

  useEffect(() => {
    listConsultations().then(async d => {
      const list = d.consultations || [];
      if (list.length) {
        const t = list[0];
        const full = await getConsultationThread(t.id);
        setThread(full.consultation); setMessages(full.messages || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages, busy]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setText('');
    setMessages(m => [...m, { id: 'tmp', sender: 'client', text: t, created_at: new Date().toISOString() }]);
    try {
      if (!thread) {
        const res = await createConsultation(t);
        setThread(res.consultation); setMessages(res.messages || []);
      } else {
        const res = await postConsultationMessage(thread.id, t);
        setMessages(res.messages || []);
      }
    } catch (err) {
      setMessages(m => [...m, { id: 'err', sender: 'ai', text: 'Zprávu se nepodařilo odeslat. Zkuste to prosím znovu.', created_at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    if (!thread) return;
    setBusy(true);
    try { await escalateConsultation(thread.id); const full = await getConsultationThread(thread.id); setThread(full.consultation); setMessages(full.messages || []); }
    catch {} finally { setBusy(false); }
  };

  if (loading) return <div style={wrap}><p style={{ color: 'var(--text-dim)' }}>Načítání…</p></div>;

  return (
    <div style={wrap}>
      <h2 style={h2}>Konzultace s M&Ai</h2>
      <p style={{ color: 'var(--text-dim)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
        Zeptejte se našeho AI poradce na cokoli kolem valuace a prodeje podniku. Když budete chtít mluvit
        s živým poradcem, klikněte na tlačítko níže a náš tým se vám ozve přímo tady.
      </p>

      <Thread messages={messages} mineSender="client" bodyRef={bodyRef} />

      <form onSubmit={send} style={{ display: 'flex', gap: '0.5rem' }}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Napište zprávu…" style={input} />
        <button type="submit" disabled={busy || !text.trim()} style={sendBtn}>Odeslat</button>
      </form>

      <div style={{ marginTop: '0.75rem' }}>
        {thread && thread.escalated ? (
          <div style={{ fontSize: '0.85rem', color: 'var(--color-success)' }}>✓ Konverzace předána poradci M&Ai. Brzy se vám ozve.</div>
        ) : (
          <button onClick={escalate} disabled={!thread || busy} style={escalateBtn}>Spojit s poradcem M&Ai</button>
        )}
      </div>
    </div>
  );
}

// ── Tým / admin ─────────────────────────────────────────────────────────────
function StaffConsultations() {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null); // consultation row
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  const loadList = () => listConsultations().then(d => setList(d.consultations || [])).catch(() => setList([]));
  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages]);

  const open = async (c) => {
    setActive(c);
    const full = await getConsultationThread(c.id);
    setMessages(full.messages || []);
  };

  const reply = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy || !active) return;
    setBusy(true); setText('');
    try { const res = await postConsultationMessage(active.id, t); setMessages(res.messages || []); }
    catch {} finally { setBusy(false); }
  };

  if (list === null) return <div style={wrap}><p style={{ color: 'var(--text-dim)' }}>Načítání…</p></div>;

  if (active) {
    return (
      <div style={wrap}>
        <button onClick={() => { setActive(null); loadList(); }} style={backBtn}>← Zpět na seznam</button>
        <h2 style={{ ...h2, marginTop: '0.75rem' }}>{active.email || 'Klient'}</h2>
        <Thread messages={messages} mineSender="team" bodyRef={bodyRef} />
        <form onSubmit={reply} style={{ display: 'flex', gap: '0.5rem' }}>
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Odpověď klientovi…" style={input} />
          <button type="submit" disabled={busy || !text.trim()} style={sendBtn}>Odeslat</button>
        </form>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <h2 style={h2}>Konzultace</h2>
      {list.length === 0 && <p style={{ color: 'var(--text-dim)' }}>Zatím žádné konzultace.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {list.map(c => (
          <button key={c.id} onClick={() => open(c)} style={{
            textAlign: 'left', background: 'var(--purple-dark)', border: '1px solid rgba(0,0,0,0.1)',
            borderLeft: `3px solid ${c.escalated ? 'var(--color-danger)' : 'rgba(0,0,0,0.1)'}`,
            borderRadius: 10, padding: '0.85rem 1rem', cursor: 'pointer',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
              <strong style={{ color: 'var(--star-white)' }}>{c.email || 'Klient'}</strong>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>{fmtTime(c.created_at)}</span>
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message}</div>
            {c.escalated && <span style={{ display: 'inline-block', marginTop: '0.35rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-danger)' }}>● Žádá poradce</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Consultation({ isStaff }) {
  return isStaff ? <StaffConsultations /> : <ClientConsultation />;
}

const wrap = { maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem', fontFamily: "'Inter', sans-serif" };
const h2 = { marginTop: 0, fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.4rem' };
const input = { flex: 1, padding: '0.7rem 0.9rem', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, fontSize: '0.92rem', outline: 'none' };
const sendBtn = { background: ACCENT, color: 'white', border: 'none', borderRadius: 8, padding: '0 1.1rem', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' };
const escalateBtn = { background: 'transparent', border: `1px solid ${ACCENT}`, color: ACCENT, borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' };
const backBtn = { background: 'transparent', border: 'none', color: ACCENT, fontSize: '0.85rem', cursor: 'pointer', padding: 0 };
